import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Download, FileText, BarChart2, TrendingUp, TrendingDown, Users } from 'lucide-react';
import { Chart } from 'chart.js/auto';

const Reports = ({ team, onSuccess }) => {
  const [startDate, setStartDate] = useState('2025-12-01');
  const [endDate, setEndDate] = useState('2026-01-16');
  
  
  const confidenceChartRef = useRef(null);
  const overridesChartRef = useRef(null);
  const confidenceChartInstance = useRef(null);
  const overridesChartInstance = useRef(null);

  // Initialize charts
  useEffect(() => {
    // AI Confidence Improvement Chart (24 weeks)
    if (confidenceChartRef.current) {
      if (confidenceChartInstance.current) {
        confidenceChartInstance.current.destroy();
      }

      const ctx = confidenceChartRef.current.getContext('2d');
      confidenceChartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: ['W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7', 'W8', 'W9', 'W10', 'W11', 'W12', 'W13', 'W14', 'W15', 'W16', 'W17', 'W18', 'W19', 'W20', 'W21', 'W22', 'W23', 'W24'],
          datasets: [{
            label: 'AI Confidence Score',
            data: [10, 15, 22, 60, 68, 72, 75, 78, 80, 82, 84, 85, 86, 87, 88, 89, 90, 90.5, 91, 91.5, 92, 92, 92, 92],
            borderColor: '#00D4AA',
            backgroundColor: 'rgba(0, 212, 170, 0.1)',
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: '#00D4AA',
            pointBorderColor: '#0A1628',
            pointBorderWidth: 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: '#0f1d35',
              titleColor: '#FFFFFF',
              bodyColor: '#00D4AA',
              borderColor: '#00D4AA',
              borderWidth: 1,
              padding: 12,
              displayColors: false,
              callbacks: {
                label: function(context) {
                  return 'Confidence: ' + context.parsed.y + '%';
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              max: 100,
              ticks: {
                color: 'rgba(255, 255, 255, 0.6)',
                callback: function(value) {
                  return value + '%';
                }
              },
              grid: {
                color: 'rgba(255, 255, 255, 0.05)'
              }
            },
            x: {
              ticks: {
                color: 'rgba(255, 255, 255, 0.6)'
              },
              grid: {
                color: 'rgba(255, 255, 255, 0.05)'
              }
            }
          }
        }
      });
    }

    // Manager Overrides Chart (6 months)
    if (overridesChartRef.current) {
      if (overridesChartInstance.current) {
        overridesChartInstance.current.destroy();
      }

      const ctx = overridesChartRef.current.getContext('2d');
      overridesChartInstance.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'],
          datasets: [{
            label: 'Manager Overrides',
            data: [47, 39, 28, 31, 19, 12],
            backgroundColor: 'rgba(251, 191, 36, 0.8)',
            borderColor: '#fbbf24',
            borderWidth: 2,
            borderRadius: 8
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: '#0f1d35',
              titleColor: '#FFFFFF',
              bodyColor: '#fbbf24',
              borderColor: '#fbbf24',
              borderWidth: 1,
              padding: 12,
              displayColors: false,
              callbacks: {
                label: function(context) {
                  return 'Overrides: ' + context.parsed.y;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                color: 'rgba(255, 255, 255, 0.6)',
                stepSize: 10
              },
              grid: {
                color: 'rgba(255, 255, 255, 0.05)'
              }
            },
            x: {
              ticks: {
                color: 'rgba(255, 255, 255, 0.6)'
              },
              grid: {
                display: false
              }
            }
          }
        }
      });
    }

    return () => {
      if (confidenceChartInstance.current) confidenceChartInstance.current.destroy();
      if (overridesChartInstance.current) overridesChartInstance.current.destroy();
    };
  }, []);

  const handleExportPDF = () => {
    onSuccess('PDF report generated successfully');
  };

  const handleExportCSV = () => {
    onSuccess('CSV export downloaded successfully');
  };

  // Sample team performance data
const teamPerformance = [
  { name: 'Maria Rodriguez', leads: 45, closed: 8, closeRate: 18, trend: 'up', avgResponse: 18, gross: '$22,400' },
  { name: 'Carlos Santos', leads: 38, closed: 7, closeRate: 19, trend: 'up', avgResponse: 15, gross: '$19,600' },
  { name: 'Vlad Petrov', leads: 52, closed: 7, closeRate: 13, trend: 'down', avgResponse: 22, gross: '$16,800' },
  { name: 'Sarah Johnson', leads: 28, closed: 5, closeRate: 18, trend: 'up', avgResponse: 25, gross: '$14,000' },
  { name: 'Mike Chen', leads: 41, closed: 6, closeRate: 15, trend: 'down', avgResponse: 20, gross: '$15,600' }
];

// Report type selector
const [reportType, setReportType] = useState('overview');
const reportTypes = [
  { value: 'overview', label: 'Performance Overview' },
  { value: 'ai-confidence', label: 'AI Confidence Analysis' },
  { value: 'team-breakdown', label: 'Team Breakdown' },
  { value: 'lead-sources', label: 'Lead Source Analysis' },
  { value: 'gross', label: 'Gross Report' }
];

return (
  <>
    <div className="header">
      <h1>Reports & Analytics</h1>
      <p>View performance metrics and generate reports</p>
    </div>

    {/* Report Type Selector */}
    <div style={{ marginBottom: '24px' }}>
      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
        Report Type
      </label>
      <select
        value={reportType}
        onChange={(e) => setReportType(e.target.value)}
        style={{
          background: '#1a2332',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '8px',
          padding: '12px 16px',
          color: '#FFFFFF',
          fontSize: '14px',
          minWidth: '300px'
        }}
      >
        {reportTypes.map(type => (
          <option key={type.value} value={type.value}>
            {type.label}
          </option>
        ))}
      </select>
    </div>

    {/* Date Range Selector */}
    <div style={{
      background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
      border: '1px solid rgba(0, 212, 170, 0.1)',
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '16px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '6px' }}>
            Start Date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{
              background: '#1a2332',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              padding: '10px 14px',
              color: '#FFFFFF',
              fontSize: '14px'
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '6px' }}>
            End Date
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{
              background: '#1a2332',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              padding: '10px 14px',
              color: '#FFFFFF',
              fontSize: '14px'
            }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={handleExportPDF}
          style={{
            background: 'linear-gradient(135deg, #00D4AA 0%, #00A88A 100%)',
            border: '2px solid rgba(0, 212, 170, 0.3)',
            borderRadius: '10px',
            padding: '10px 20px',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FileText size={16} />
          Export PDF
        </button>
        <button
          onClick={handleExportCSV}
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '10px',
            padding: '10px 20px',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>
    </div>

    {/* Charts Grid */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '24px', marginBottom: '32px' }}>
      {/* AI Confidence Chart */}
      <div style={{
        background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
        border: '1px solid rgba(0, 212, 170, 0.1)',
        borderRadius: '16px',
        padding: '24px'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#FFFFFF', marginBottom: '4px' }}>
            AI Confidence Improvement
          </h3>
          <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>
            24-week trend showing AI learning progress
          </p>
        </div>
        <div style={{ height: '300px', position: 'relative' }}>
          <canvas ref={confidenceChartRef}></canvas>
        </div>
      </div>

      {/* Manager Overrides Chart */}
      <div style={{
        background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
        border: '1px solid rgba(0, 212, 170, 0.1)',
        borderRadius: '16px',
        padding: '24px'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#FFFFFF', marginBottom: '4px' }}>
            Manager Overrides
          </h3>
          <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>
            6-month trend showing decreasing manual interventions
          </p>
        </div>
        <div style={{ height: '300px', position: 'relative' }}>
          <canvas ref={overridesChartRef}></canvas>
        </div>
      </div>
    </div>

    {/* Team Performance Table */}
    <div style={{
      background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
      border: '1px solid rgba(0, 212, 170, 0.1)',
      borderRadius: '16px',
      padding: '24px'
    }}>
      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#FFFFFF', marginBottom: '20px' }}>
        Team Performance Breakdown
      </h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid rgba(0, 212, 170, 0.2)' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.6)' }}>
                Team Member
              </th>
              <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.6)' }}>
                Leads Assigned
              </th>
              <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.6)' }}>
                Closed Won
              </th>
              <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.6)' }}>
                Close Rate
              </th>
              <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.6)' }}>
                Avg Response
              </th>
              <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.6)' }}>
  Gross
</th>
            </tr>
          </thead>
          <tbody>
            {teamPerformance.map((member, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                <td style={{ padding: '16px 12px', fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>
                  {member.name}
                </td>
                <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>
                  {member.leads}
                </td>
                <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>
                  {member.closed}
                </td>
                <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <span style={{ color: member.trend === 'up' ? '#00D4AA' : '#ef4444' }}>
                    {member.closeRate}%
                  </span>
                  {member.trend === 'up' ? (
                    <TrendingUp size={16} color="#00D4AA" />
                  ) : (
                    <TrendingDown size={16} color="#ef4444" />
                  )}
                </td>
                <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>
                  {member.avgResponse}m
                </td>
                <td style={{ padding: '16px 12px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#00D4AA' }}>
                  {member.gross}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid rgba(0, 212, 170, 0.2)' }}>
              <td style={{ padding: '16px 12px', fontSize: '14px', fontWeight: '700', color: '#FFFFFF' }}>
                Total
              </td>
              <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: '14px', fontWeight: '700', color: '#00D4AA' }}>
                204
              </td>
              <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: '14px', fontWeight: '700', color: '#00D4AA' }}>
                33
              </td>
              <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: '14px', fontWeight: '700', color: '#00D4AA' }}>
                16.2%
              </td>
              <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: '14px', fontWeight: '700', color: '#00D4AA' }}>
                20m
              </td>
              <td style={{ padding: '16px 12px', textAlign: 'right', fontSize: '14px', fontWeight: '700', color: '#00D4AA' }}>
  $88,400
</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </>
);}

export default Reports;
